import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardMedia,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Fab,
} from '@mui/material';
import {
  Add,
  PhotoCamera,
  CloudUpload,
  Download,
  Delete,
  Visibility,
  Search,
  FilterList,
  LocationOn,
  CalendarToday,
  Person,
  Folder,
} from '@mui/icons-material';
import { photoDocumentationService, Photo, PhotoFilters } from '../services/photoDocumentationService';
import useCompanySelector from '../hooks/useCompanySelector';
import { API_BASE_URL } from '../services/config';

// Photo interface is now imported from photoDocumentationService

const PhotoDocumentation: React.FC = () => {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [projectFilter, setProjectFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  useEffect(() => {
    fetchPhotos();
    fetchProjects();
  }, []);

  // Extract a helpful error message from API errors
  const getApiErrorMessage = (err: any, defaultMessage: string) => {
    try {
      const resp = err?.response;
      const data = resp?.data;
      if (!resp) return err?.message || defaultMessage;
      if (typeof data === 'string' && data.trim().length > 0) return data;
      if (data?.message) return data.message;
      if (data?.error) return data.error;
      if (Array.isArray(data?.errors) && data.errors.length > 0) {
        const msgs = data.errors
          .map((e: any) => e?.msg || e?.message || JSON.stringify(e))
          .filter(Boolean);
        return msgs.join('\n');
      }
      return err?.message || defaultMessage;
    } catch (_) {
      return defaultMessage;
    }
  };

  const fetchPhotos = async () => {
    try {
      setLoading(true);
      setError(null);
      const payload = await photoDocumentationService.getPhotos();
      const items = Array.isArray((payload as any)?.photos)
        ? (payload as any).photos
        : Array.isArray((payload as any)?.data)
          ? (payload as any).data
          : Array.isArray(payload as any)
            ? (payload as any)
            : [];
      setPhotos(items);
    } catch (err) {
      console.error('Error fetching photos:', err);
      setError('Failed to load photos');
      // Do not fallback to mock data; keep empty array
      setPhotos([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch projects to provide valid project IDs for uploads
  type ProjectOption = { _id: string; name: string; company?: any };
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [loadingProjects, setLoadingProjects] = useState<boolean>(false);
  // Admin company selection
  const { isAdmin, companies: companyOpts } = useCompanySelector();
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('all');

  // Keep selected company valid when options update
  useEffect(() => {
    if (isAdmin && selectedCompanyId !== 'all' && !companyOpts.some(c => c.id === selectedCompanyId)) {
      setSelectedCompanyId('all');
    }
  }, [isAdmin, companyOpts, selectedCompanyId]);
  const fetchProjects = async () => {
    try {
      setLoadingProjects(true);
      const token = localStorage.getItem('token');
      const projHeaders: Record<string, string> = {};
      if (token) projHeaders.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API_BASE_URL}/projects`, { headers: projHeaders });
      if (!res.ok) throw new Error('Failed to fetch projects');
      const payload = await res.json();
      const items: any[] = Array.isArray(payload?.data)
        ? payload.data
        : (Array.isArray(payload?.projects) ? payload.projects : []);
      const mapped: ProjectOption[] = items
        .filter((p: any) => p && typeof p === 'object')
        .map((p: any) => ({ _id: p._id, name: p.name, company: p.company }));
      setProjects(mapped);
      // Auto-select a valid project based on selected company (admin) or any with company
      const companyId = selectedCompanyId !== 'all' ? selectedCompanyId : null;
      const normalizedId = (c: any) => (typeof c === 'string' ? c : (c?._id || c?.id));
      const valid = mapped.filter((p) => !!p.company && (!companyId || normalizedId(p.company) === companyId));
      if (projectFilter === 'all' && valid.length > 0) {
        setProjectFilter(valid[0]._id);
      }
    } catch (err) {
      console.error('Error fetching projects:', err);
      setProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  };

  const handleUploadPhoto = async (file: File, metadata: Partial<Photo>) => {
    try {
      setLoading(true);
      const payload: any = { ...metadata };
      if (isAdmin && selectedCompanyId && selectedCompanyId !== 'all') {
        payload.company = selectedCompanyId;
      }
      await photoDocumentationService.uploadPhoto(file, payload as any);
      await fetchPhotos(); // Refresh the list
    } catch (err) {
      console.error('Error uploading photo:', err);
      setError(getApiErrorMessage(err, 'Failed to upload photo'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePhoto = async (photoId: string) => {
    try {
      await photoDocumentationService.deletePhoto(photoId);
      await fetchPhotos(); // Refresh the list
    } catch (err) {
      console.error('Error deleting photo:', err);
      setError('Failed to delete photo');
    }
  };

  const handleDownloadPhoto = async (photoId: string, filename: string) => {
    try {
      await photoDocumentationService.downloadPhoto(photoId);
    } catch (err) {
      console.error('Error downloading photo:', err);
      setError('Failed to download photo');
    }
  };

  const handleExportPhotos = async () => {
    try {
      const filters: PhotoFilters = {
        search: searchTerm || undefined,
        project: projectFilter !== 'all' ? projectFilter : undefined,
        category: categoryFilter !== 'all' ? categoryFilter as any : undefined
      };
      await photoDocumentationService.exportPhotosData(filters);
    } catch (err) {
      console.error('Error exporting photos:', err);
      setError('Failed to export photos');
    }
  };

  // Ensure photos is always an array
  const photosArray = Array.isArray(photos) ? photos : [];

  const filteredPhotos = photosArray.filter((photo) => {
    const safeTitle = (photo?.title || '').toLowerCase();
    const safeDesc = (photo?.description || '').toLowerCase();
    const safeTags = Array.isArray(photo?.tags) ? photo.tags : [];
    const matchesSearch = safeTitle.includes(searchTerm.toLowerCase()) ||
                         safeDesc.includes(searchTerm.toLowerCase()) ||
                         safeTags.some(tag => (tag || '').toLowerCase().includes(searchTerm.toLowerCase()));
    const photoProjectId = typeof photo.project === 'string' ? photo.project : photo.project?._id;
    const matchesProject = projectFilter === 'all' || photoProjectId === projectFilter;
    const matchesCategory = categoryFilter === 'all' || photo.category === categoryFilter;
    return matchesSearch && matchesProject && matchesCategory;
  });

  // Removed summary metrics (counts) from the page

  const handleViewPhoto = (photo: Photo) => {
    setSelectedPhoto(photo);
    setViewDialogOpen(true);
  };

  const getCategoryColor = (category: string) => {
    const colors = {
      progress: 'primary',
      safety: 'warning',
      quality: 'success',
      issue: 'error',
      completion: 'info',
      before: 'secondary',
      after: 'secondary',
      inspection: 'info',
    };
    return colors[category as keyof typeof colors] || 'default';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Placeholder image for demo purposes
  const getPlaceholderImage = (category: string) => {
    const colors = {
      progress: '#2196f3',
      safety: '#ff9800',
      quality: '#4caf50',
      issue: '#f44336',
      completion: '#00bcd4',
      before_after: '#9c27b0',
    };
    return `data:image/svg+xml;base64,${btoa(`
      <svg width="400" height="300" xmlns="http://www.w3.org/2000/svg">
        <rect width="100%" height="100%" fill="${colors[category as keyof typeof colors] || '#gray'}"/>
        <text x="50%" y="50%" font-family="Arial" font-size="16" fill="white" text-anchor="middle" dy=".3em">
          ${category.toUpperCase()} PHOTO
        </text>
      </svg>
    `)}`;
  };

  if (loading && photosArray.length === 0) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Typography>Loading photos...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {error && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'error.light', color: 'error.contrastText', borderRadius: 1 }}>
          <Typography>{error}</Typography>
          <Button 
            size="small" 
            onClick={() => setError(null)} 
            sx={{ mt: 1, color: 'inherit' }}
          >
            Dismiss
          </Button>
        </Box>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Photo Documentation
        </Typography>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<CloudUpload />}
          disabled={loading}
          onClick={() => {
              // This would typically open a file picker dialog
              const input = document.createElement('input');
              input.type = 'file';
              input.multiple = true;
              input.accept = 'image/*';
              input.onchange = (e) => {
                const files = (e.target as HTMLInputElement).files;
                if (files) {
                  if (projectFilter === 'all') {
                    setError('Please select a project before uploading photos.');
                    return;
                  }
                  const selectedProject = projects.find((p) => p._id === projectFilter);
                  const hasProjectCompany = !!selectedProject?.company;
                  const adminProvidedCompany = isAdmin && selectedCompanyId && selectedCompanyId !== 'all';
                  if (!hasProjectCompany && !adminProvidedCompany) {
                    setError('Selected project has no company associated. Choose another project or contact an admin.');
                    return;
                  }
                  Array.from(files).forEach(file => {
                    handleUploadPhoto(file, {
                      title: file.name,
                      category: 'progress',
                      project: projectFilter as string,
                      ...(adminProvidedCompany ? { company: selectedCompanyId } : {})
                    });
                  });
                }
              };
              input.click();
            }}
          >
            Upload Photos
          </Button>
        </Box>
      </Box>

      {/* Warn when no valid projects available */}
      {!loadingProjects && projects.filter((p) => !!p.company).length === 0 && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'warning.light', color: 'warning.contrastText', borderRadius: 1 }}>
          <Typography>
            No projects with an associated company are available. Please assign a company to your project or contact an admin.
          </Typography>
        </Box>
      )}

      {/* Summary metrics removed as requested */}

      {/* Filters and Search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            placeholder="Search photos..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />,
            }}
            sx={{ minWidth: 250 }}
          />
          {isAdmin && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Company</InputLabel>
              <Select
                value={selectedCompanyId}
                label="Company"
                onChange={(e) => {
                  const val = String(e.target.value);
                  setSelectedCompanyId(val);
                  // Reset project if it no longer matches selected company
                  const normalizedId = (c: any) => (typeof c === 'string' ? c : (c?._id || c?.id));
                  const currentProject = projects.find((p) => p._id === projectFilter);
                  if (currentProject && val !== 'all' && normalizedId(currentProject.company) !== val) {
                    setProjectFilter('all');
                  }
                }}
              >
                <MenuItem value="all">All Companies</MenuItem>
                {companyOpts.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Project</InputLabel>
            <Select
              value={projectFilter}
              label="Project"
              onChange={(e) => setProjectFilter(e.target.value)}
            >
              <MenuItem value="all">All Projects</MenuItem>
              {projects
                .filter((p) => !!p.company)
                .filter((p) => {
                  if (!isAdmin) return true;
                  if (!selectedCompanyId || selectedCompanyId === 'all') return true;
                  const cid = typeof p.company === 'string' ? p.company : (p.company?._id || p.company?.id);
                  return cid === selectedCompanyId;
                })
                .map((p) => (
                <MenuItem key={p._id} value={p._id}>
                  {p.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={categoryFilter}
              label="Category"
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <MenuItem value="all">All Categories</MenuItem>
              <MenuItem value="progress">Progress</MenuItem>
              <MenuItem value="safety">Safety</MenuItem>
              <MenuItem value="quality">Quality</MenuItem>
              <MenuItem value="issue">Issue</MenuItem>
              <MenuItem value="completion">Completion</MenuItem>
              <MenuItem value="before">Before</MenuItem>
              <MenuItem value="after">After</MenuItem>
              <MenuItem value="inspection">Inspection</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            sx={{ ml: 'auto' }}
          >
            More Filters
          </Button>
          <Button
            variant="outlined"
            startIcon={<Download />}
            disabled={loading}
            onClick={handleExportPhotos}
          >
            Export Photos
          </Button>
        </Box>
      </Paper>

      {/* Photo Grid */}
      {filteredPhotos.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <PhotoCamera sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No photos found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {searchTerm || projectFilter !== 'all' || categoryFilter !== 'all' 
              ? 'Try adjusting your filters or search terms'
              : 'Upload your first photo to get started'
            }
          </Typography>
        </Box>
      ) : (
        <ImageList variant="masonry" cols={4} gap={16}>
        {filteredPhotos.map((photo) => (
          <ImageListItem key={photo._id}>
            <img
              src={getPlaceholderImage(photo.category)}
              alt={photo.title}
              loading="lazy"
              style={{ cursor: 'pointer' }}
              onClick={() => handleViewPhoto(photo)}
            />
            <ImageListItemBar
              title={photo.title}
              subtitle={
                <Box>
                  <Typography variant="caption" display="block">
                    {typeof photo.project === 'string' ? photo.project : photo.project.name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <Chip
                      label={photo.category.replace('_', ' ')}
                      size="small"
                      color={getCategoryColor(photo.category) as any}
                      sx={{ textTransform: 'capitalize', fontSize: '0.7rem', height: 20 }}
                    />
                    <Typography variant="caption">
                      {formatDate(photo.createdAt)}
                    </Typography>
                  </Box>
                </Box>
              }
              actionIcon={
                <Box>
                  <Tooltip title="View Details">
                    <IconButton
                      sx={{ color: 'rgba(255, 255, 255, 0.54)' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewPhoto(photo);
                      }}
                    >
                      <Visibility />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Download">
                    <IconButton
                      sx={{ color: 'rgba(255, 255, 255, 0.54)' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadPhoto(photo._id, photo.filename);
                      }}
                    >
                      <Download />
                    </IconButton>
                  </Tooltip>
                </Box>
              }
            />
          </ImageListItem>
        ))}
        </ImageList>
      )}

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="add photo"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
      >
        <Add />
      </Fab>

      {/* View Photo Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Photo Details - {selectedPhoto?.title}
        </DialogTitle>
        <DialogContent>
          {selectedPhoto && (
            <Box sx={{ pt: 2 }}>
              <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
                <Box sx={{ flex: 1 }}>
                  <img
                    src={getPlaceholderImage(selectedPhoto.category)}
                    alt={selectedPhoto.title}
                    style={{ width: '100%', maxHeight: 400, objectFit: 'contain' }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" gutterBottom>
                    {selectedPhoto.title}
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {selectedPhoto.description}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Project
                      </Typography>
                      <Typography variant="body1">
                        {typeof selectedPhoto.project === 'string' ? selectedPhoto.project : selectedPhoto.project.name}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Location
                      </Typography>
                      <Typography variant="body1">
                        {selectedPhoto.location}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Photographer
                      </Typography>
                      <Typography variant="body1">
                        {typeof selectedPhoto.uploadedBy === 'string' ? selectedPhoto.uploadedBy : selectedPhoto.uploadedBy.name}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Date Taken
                      </Typography>
                      <Typography variant="body1">
                        {formatDate(selectedPhoto.createdAt)}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Category
                      </Typography>
                      <Chip
                        label={selectedPhoto.category.replace('_', ' ')}
                        color={getCategoryColor(selectedPhoto.category) as any}
                        size="small"
                        sx={{ textTransform: 'capitalize', mt: 0.5 }}
                      />
                    </Box>
                    
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Tags
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                        {(Array.isArray(selectedPhoto.tags) ? selectedPhoto.tags : []).map((tag) => (
                          <Chip
                            key={tag}
                            label={tag}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </Box>
                    
                    <Box sx={{ display: 'flex', gap: 3 }}>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          File Size
                        </Typography>
                        <Typography variant="body2">
                          {(selectedPhoto.size / 1024 / 1024).toFixed(2)} MB
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Dimensions
                        </Typography>
                        <Typography variant="body2">
                          N/A
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          <Button startIcon={<Download />}>
            Download
          </Button>
          <Button 
            color="error" 
            startIcon={<Delete />}
            onClick={() => {
              if (selectedPhoto && window.confirm('Are you sure you want to delete this photo?')) {
                handleDeletePhoto(selectedPhoto._id);
                setViewDialogOpen(false);
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PhotoDocumentation;